/**
 * JourneyCompetencyRunner — pilote l'enchaînement parcours → série.
 *
 * Quand un utilisateur clique sur « Commencer Facile » d'une compétence
 * dans `CurriculumJourney`, on :
 *   1. Démarre une session côté backend (POST /learning/series/start)
 *   2. Récupère les exercices de la compétence à la difficulté donnée
 *   3. Affiche `ExerciseSeriesPlayer` avec ces items
 *   4. À la fin (ou exit), POST /learning/series/{id}/complete et on
 *      remonte le rafraîchissement parent
 */
import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { learningApi } from '../../services/api';
import { ExerciseSeriesPlayer } from './ExerciseSeriesPlayer';

const SERIES_LIMIT = 10;

export const JourneyCompetencyRunner = ({
  competency,
  difficulty,
  onExit,
  testidPrefix = 'journey-runner',
}) => {
  const [items, setItems] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const exRes = await learningApi.competencyExercises(competency.id, difficulty);
        const list = (exRes.data?.items || []).slice(0, SERIES_LIMIT);
        if (cancel) return;
        if (!list.length) {
          setError("Aucun exercice disponible pour ce niveau.");
          setItems([]);
          setLoading(false);
          return;
        }
        setItems(list);
        try {
          const sess = await learningApi.startSeries({
            competencyId: competency.id,
            difficulty,
            exerciseIds: list.map((x) => x.id),
          });
          if (!cancel) setSessionId(sess.data?.id || null);
        } catch { /* still allow play even if session not recorded */ }
      } catch {
        if (!cancel) setError("Impossible de charger les exercices.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [competency.id, difficulty]);

  const handleExit = async (summary = null) => {
    if (sessionId) {
      try {
        await learningApi.completeSeries(sessionId, {
          score: summary?.percent ?? 0,
          correct: summary?.correct ?? 0,
          wrong: summary?.wrong ?? 0,
          answeredExerciseIds: summary?.answeredIds || [],
        });
      } catch { /* best effort */ }
    }
    onExit && onExit();
  };

  return (
    <div data-testid={testidPrefix}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleExit(null)}
          className="text-text-muted"
          data-testid={`${testidPrefix}-back`}
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour au parcours
        </Button>
        <div className="text-xs text-text-muted">
          {competency.title?.fr} · niveau {difficulty}
        </div>
      </div>

      {loading && (
        <div className="rounded-lg border border-border bg-surface-elevated/30 p-6 text-sm text-text-muted">
          Préparation de la série…
        </div>
      )}
      {!loading && error && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-6 text-sm text-rose-200"
             data-testid={`${testidPrefix}-error`}>
          {error}
        </div>
      )}
      {!loading && items?.length > 0 && (
        <ExerciseSeriesPlayer
          exercises={items}
          testidPrefix={`${testidPrefix}-player`}
          onExit={() => handleExit(null)}
          onComplete={(s) => handleExit(s)}
        />
      )}
    </div>
  );
};

export default JourneyCompetencyRunner;
